/**
 * /reviews — public wall of client voices with in-page submission modal.
 *
 * Loads paginated approved reviews from `/api/reviews`. Renders an
 * AggregateRating JSON-LD block from the API's aggregate response so the
 * page is SEO-crawlable even though review cards mount client-side.
 *
 * Submission is a modal opened from either the hero "Leave a review" CTA
 * or the footer CTA at the bottom of the list. On success the modal
 * shows a thank-you note and offers to close.
 */

import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, MessageSquarePlus } from "lucide-react";
import SectionLabel from "../components/SectionLabel";
import SEO from "../components/SEO";
import StructuredData from "../components/StructuredData";
import ReviewCard from "../components/reviews/ReviewCard";
import ReviewForm from "../components/reviews/ReviewForm";
import ReviewPhotoLightbox from "../components/reviews/ReviewPhotoLightbox";
import StarRating from "../components/reviews/StarRating";
import { reviewsApi } from "../lib/reviewsApi";

const PER_PAGE = 10;

function buildAggregateSchema(aggregate) {
    if (!aggregate || aggregate.count <= 0) return null;
    return {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "Advanced Connected Technologies",
        url: "https://actluxe.net",
        aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: aggregate.avg,
            reviewCount: aggregate.count,
            bestRating: 5,
            worstRating: 1,
        },
    };
}

export default function Reviews() {
    const [reviews, setReviews] = useState([]);
    const [aggregate, setAggregate] = useState({ avg: 0, count: 0 });
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [hasNext, setHasNext] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [showForm, setShowForm] = useState(false);

    // Lightbox
    const [lightbox, setLightbox] = useState({ open: false, photos: [], index: 0 });
    const openLightbox = useCallback((photos, index) => {
        setLightbox({ open: true, photos, index });
    }, []);
    const closeLightbox = useCallback(
        () => setLightbox((s) => ({ ...s, open: false })),
        [],
    );
    const setLightboxIndex = useCallback(
        (i) => setLightbox((s) => ({ ...s, index: i })),
        [],
    );

    const load = useCallback(async (targetPage) => {
        setLoading(true);
        setError("");
        try {
            const data = await reviewsApi.listPublicReviews({
                page: targetPage,
                perPage: PER_PAGE,
            });
            setReviews(data.reviews || []);
            setAggregate(data.aggregate || { avg: 0, count: 0 });
            setTotal(data.total || 0);
            setHasNext(!!data.has_next);
            setPage(targetPage);
        } catch (err) {
            setError(err.message || "Failed to load reviews.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load(1);
    }, [load]);

    const onFormSuccess = () => {
        // Refresh so the aggregate count (pending) still stays right and
        // the page reflects any recently-approved additions.
        load(page);
    };

    const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));
    const aggregateSchema = buildAggregateSchema(aggregate);

    return (
        <div data-testid="reviews-page">
            <SEO
                title="Client Reviews — Advanced Connected Technologies"
                description="Read what our clients say about ACT — the concierge-tier smart-home integration studio. Leave a review of your engagement with our team."
                canonical="https://actluxe.net/reviews"
            />
            {aggregateSchema && <StructuredData data={aggregateSchema} />}

            {/* Hero */}
            <section
                className="relative bg-[#0B0B0C] pt-24 md:pt-32 pb-16 md:pb-20 border-b border-[rgba(201,168,106,0.12)] overflow-hidden"
                data-testid="reviews-hero"
            >
                <div className="shell">
                    <SectionLabel number="Voices" label="Client Reviews" />
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-20 mt-8 items-end">
                        <h1 className="lg:col-span-8 font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.05] max-w-4xl">
                            {"Voices of the "}
                            <span className="italic text-[#C9A86A]">
                                residence.
                            </span>
                        </h1>
                        <p className="lg:col-span-4 text-[#9A958A] text-base md:text-lg leading-[1.85] max-w-md lg:pb-3">
                            A private record of the residents we have had the
                            privilege of serving. Read their words — or add
                            your own.
                        </p>
                    </div>

                    {aggregate.count > 0 && (
                        <div
                            className="mt-14 flex flex-wrap items-center gap-6 md:gap-10 pt-8 border-t border-[rgba(201,168,106,0.15)]"
                            data-testid="reviews-aggregate"
                        >
                            <div className="flex items-center gap-4">
                                <div className="font-serif text-4xl md:text-5xl text-[#C9A86A] tabular-nums leading-none">
                                    {aggregate.avg.toFixed(1)}
                                </div>
                                <div>
                                    <StarRating
                                        value={Math.round(aggregate.avg)}
                                        size={18}
                                    />
                                    <div className="mt-2 text-[10.5px] tracking-[0.28em] uppercase text-[#9A958A]">
                                        Based on {aggregate.count} review
                                        {aggregate.count === 1 ? "" : "s"}
                                    </div>
                                </div>
                            </div>

                            <div className="ml-auto flex flex-col items-end gap-2">
                                <button
                                    type="button"
                                    onClick={() => setShowForm(true)}
                                    data-testid="reviews-open-form-hero"
                                    className="inline-flex items-center gap-2 border border-[#C9A86A] text-[#C9A86A] hover:bg-[#C9A86A] hover:text-[#0B0B0C] text-[11px] tracking-[0.32em] uppercase px-6 py-3 transition-all duration-300"
                                >
                                    <MessageSquarePlus size={14} strokeWidth={1.5} />
                                    Leave a review
                                </button>
                                <a
                                    href="/leave-a-review"
                                    target="_blank"
                                    rel="noopener"
                                    data-testid="open-full-review-form"
                                    className="text-[10.5px] tracking-[0.24em] uppercase text-[#9A958A] hover:text-[#C9A86A] transition-colors"
                                >
                                    or open in a new tab &rarr;
                                </a>
                            </div>
                        </div>
                    )}
                </div>
            </section>

            {/* Reviews list */}
            <section className="bg-[#0B0B0C] py-20 md:py-28">
                <div className="shell">
                    {error && (
                        <div
                            role="alert"
                            className="border border-[rgba(224,168,154,0.4)] text-[#E0A89A] px-4 py-3 mb-8 text-[13px]"
                            data-testid="reviews-error"
                        >
                            {error}
                        </div>
                    )}

                    {loading && reviews.length === 0 ? (
                        <div
                            className="text-[#9A958A] text-sm py-16 text-center"
                            data-testid="reviews-loading"
                        >
                            Loading reviews…
                        </div>
                    ) : reviews.length === 0 && !error ? (
                        <div
                            className="text-[#9A958A] text-center py-24 max-w-lg mx-auto"
                            data-testid="reviews-empty"
                        >
                            <p className="font-serif italic text-2xl text-[#F3EFE7] mb-6">
                                Be the first voice.
                            </p>
                            <p className="text-sm leading-[1.85] mb-8">
                                Our wall is quiet — we invite you to share the
                                first review of your engagement with our team.
                            </p>
                            <button
                                type="button"
                                onClick={() => setShowForm(true)}
                                data-testid="reviews-open-form-empty"
                                className="inline-flex items-center gap-2 border border-[#C9A86A] text-[#C9A86A] hover:bg-[#C9A86A] hover:text-[#0B0B0C] text-[11px] tracking-[0.32em] uppercase px-6 py-3 transition-all duration-300"
                            >
                                <MessageSquarePlus size={14} strokeWidth={1.5} />
                                Leave a review
                            </button>
                        </div>
                    ) : (
                        <div
                            className="space-y-8"
                            data-testid="reviews-list"
                        >
                            {reviews.map((r) => (
                                <ReviewCard
                                    key={r.id}
                                    review={r}
                                    onOpenLightbox={openLightbox}
                                />
                            ))}
                        </div>
                    )}

                    {/* Pagination */}
                    {(page > 1 || hasNext) && (
                        <div
                            className="mt-16 flex items-center justify-between text-[11px] tracking-[0.28em] uppercase text-[#9A958A]"
                            data-testid="reviews-pagination"
                        >
                            <button
                                type="button"
                                disabled={page <= 1}
                                onClick={() => load(page - 1)}
                                className="hover:text-[#C9A86A] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                data-testid="reviews-prev"
                            >
                                &larr; Previous
                            </button>
                            <span data-testid="reviews-page-indicator">
                                Page {page} of {totalPages}
                            </span>
                            <button
                                type="button"
                                disabled={!hasNext}
                                onClick={() => load(page + 1)}
                                className="hover:text-[#C9A86A] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                data-testid="reviews-next"
                            >
                                Next &rarr;
                            </button>
                        </div>
                    )}
                </div>
            </section>

            {/* Bottom CTA */}
            {reviews.length > 0 && (
                <section className="bg-[#0B0B0C] border-t border-[rgba(201,168,106,0.15)] py-20 md:py-24">
                    <div className="shell text-center">
                        <h2 className="font-serif text-3xl md:text-4xl text-[#F3EFE7] italic mb-6">
                            Share your voice.
                        </h2>
                        <p className="text-[#9A958A] text-sm md:text-base leading-[1.85] max-w-md mx-auto mb-8">
                            If ACT has served your residence, we would be
                            honoured to hear from you.
                        </p>
                        <button
                            type="button"
                            onClick={() => setShowForm(true)}
                            data-testid="reviews-open-form-footer"
                            className="inline-flex items-center gap-2 bg-[#C9A86A] hover:bg-[#D9BC80] text-[#0B0B0C] text-[11px] tracking-[0.32em] uppercase px-8 py-4 transition-colors"
                        >
                            <MessageSquarePlus size={14} strokeWidth={1.5} />
                            Leave a review
                        </button>
                    </div>
                </section>
            )}

            {/* Submission modal */}
            <AnimatePresence>
                {showForm && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="fixed inset-0 z-[70] bg-[#0B0B0C]/85 backdrop-blur-md overflow-y-auto py-8 px-4"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="review-form-title"
                        data-testid="review-form-modal"
                        onClick={(e) => {
                            if (e.target === e.currentTarget) setShowForm(false);
                        }}
                    >
                        <motion.div
                            initial={{ y: 24, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={{ y: 12, opacity: 0 }}
                            transition={{ duration: 0.3, ease: "easeOut" }}
                            className="max-w-2xl mx-auto bg-[#15161A] border border-[rgba(201,168,106,0.3)] p-8 md:p-12"
                        >
                            <div className="flex items-start justify-between mb-8">
                                <div>
                                    <div className="text-[10.5px] tracking-[0.32em] uppercase text-[#C9A86A] mb-3">
                                        Leave a review
                                    </div>
                                    <h2
                                        id="review-form-title"
                                        className="font-serif text-3xl md:text-4xl text-[#F3EFE7]"
                                    >
                                        Share your voice.
                                    </h2>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setShowForm(false)}
                                    aria-label="Close review form"
                                    className="text-[#9A958A] hover:text-[#C9A86A] transition-colors p-2"
                                    data-testid="review-form-close"
                                >
                                    <X size={22} strokeWidth={1.4} />
                                </button>
                            </div>
                            <ReviewForm onSuccess={onFormSuccess} />
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {lightbox.open && (
                <ReviewPhotoLightbox
                    photos={lightbox.photos}
                    index={lightbox.index}
                    onClose={closeLightbox}
                    onIndex={setLightboxIndex}
                />
            )}
        </div>
    );
}
